import {
  Input,
  OnInit,
  inject,
  Component,
  ViewChild,
  DestroyRef,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { Store } from '@ngrx/store';
import { SuiPopoverTriggerDirective } from '@sui/popover';

import { toUnix } from '../../utils/date-utils';
import { relativePeriodLabel } from '../../utils/period-preset.utils';
import {
  DEFAULT_TIME_PRESETS,
  TimePresetOption,
  presetMinutes,
} from '@sensika/ag-grid-kit';
import { TranslateService } from '@ngx-translate/core';
import { Period } from '../../../core/models/core.models';
import { NomObjectClass } from '../../../core/constants';

import {
  periodChanged,
  applyDefaultPeriod,
  selectPreviousPeriod,
  removePeriodFromHistory,
} from '../../../core/actions/core.actions';
import { PeriodHistoryEntry } from '../period-history/period-history.component';
import { DashboardPagePersistenceService } from 'src/app/core/services/dashboard-page-persistence.service';
import { ObjectPersistenceService } from 'src/app/core/services/object-persistence.service';

import {
  selectPeriod,
  selectPeriodPreset,
  selectCoreLanguage,
  selectPeriodsHistory,
  selectIsDefaultPeriod,
  selectPeriodLocked,
  selectCompanyDateBounds,
} from '../../../core/reducers';
import { AppState } from 'src/app/reducers';

import { addDays, endOfMinute, startOfMinute } from 'date-fns';

import { map, Observable, tap } from 'rxjs';

@Component({
  selector: 'app-datepicker-range',
  templateUrl: './date-range-picker.component.html',
  styleUrls: ['./date-range-picker.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class DateRangePickerComponent implements OnInit {
  @ViewChild(SuiPopoverTriggerDirective)
  private dropdown: SuiPopoverTriggerDirective;

  @Input() lockPeriod: boolean = true;
  @Input() context: number;
  /** Second preset row with now-relative minute / hour durations (the operational views). */
  @Input() timePresetBar: boolean = false;
  /** The time preset row's options when the bar is on; defaults to the shared 5m .. 12h row. */
  @Input() timePresets: TimePresetOption[] = DEFAULT_TIME_PRESETS;
  /** date-fns format of the absolute range on the trigger. */
  @Input() displayFormat: string = 'dd MMM yyyy, HH:mm';

  private readonly translateService = inject(TranslateService);

  period$: Observable<Period>;
  /** The preset behind the current period; a now-relative one labels the trigger "Last 1 hour" (WAP-1763). */
  periodPreset$: Observable<string | null>;
  /** The preset of the range picked in the open panel, applied together with it. */
  private pendingPreset: string | null = null;
  /** The preset the panel opens highlighted with: the store's, until a history pick clears it. */
  panelPreset: string | null = null;
  isPeriodLocked$: Observable<boolean>;
  periodHistory$: Observable<Period[]>;
  isDefaultPeriod$: Observable<boolean>;
  companyBounds$: Observable<{ min: Date | null; max: Date }>;

  fromDate = new Date();
  toDate = addDays(this.fromDate, 2);

  isDirtyPeriod = false; // if period is not the default object period
  isPeriodLocked: boolean = false;
  isPeriodLockedInitial: boolean = false;
  periodsHistory: { startTimestamp: number; endTimestamp: number }[] = [];
  isRtl: boolean;
  NomObjectClass = NomObjectClass;

  private destroyRef = inject(DestroyRef);
  private pagePersistence = inject(DashboardPagePersistenceService);
  private objectPersistence = inject(ObjectPersistenceService);

  constructor(
    private store: Store<AppState>,
    private cdf: ChangeDetectorRef,
  ) {
    this.period$ = this.store.select(selectPeriod);
    this.periodPreset$ = this.store.select(selectPeriodPreset);
    this.periodPreset$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((preset) => {
        this.panelPreset = preset;
        this.pendingPreset = preset;
      });
    this.isPeriodLocked$ = this.store.select(selectPeriodLocked);
    this.isDefaultPeriod$ = this.store.select(selectIsDefaultPeriod);
    this.periodHistory$ = this.store.select(selectPeriodsHistory);
    this.companyBounds$ = this.store.select(selectCompanyDateBounds);
    this.store
      .select(selectPeriodsHistory)
      .pipe(
        map((periods) => periods.slice(0, 5)), // ← trim each array to max 5 items
        tap((periods) => {
          this.periodsHistory = periods;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    this.store
      .select(selectCoreLanguage)
      .pipe(
        tap((language) => {
          this.isRtl = language.rtl;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  ngOnInit(): void {
    this.period$
      .pipe(
        tap((period) => {
          if (period) {
            this.fromDate = new Date(period.startTimestamp * 1000);
            this.toDate = new Date(period.endTimestamp * 1000);

            this.cdf.detectChanges();
          }
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
    this.isPeriodLocked$
      .pipe(
        tap((periodLocked) => {
          this.isPeriodLocked = periodLocked;
          this.isPeriodLockedInitial = periodLocked;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  applyDateRange() {
    this.store.dispatch(
      periodChanged({
        period: {
          startTimestamp: toUnix(this.fromDate),
          endTimestamp: toUnix(this.toDate),
        },
        preset: this.pendingPreset,
      }),
    );

    if (
      this.isPeriodLocked ||
      this.isPeriodLockedInitial !== this.isPeriodLocked
    ) {
      this.objectPersistence.togglePeriodLocked(this.isPeriodLocked, {
        startTimestamp: toUnix(this.fromDate),
        endTimestamp: toUnix(this.toDate),
      });
    }
    this.cancel();
  }

  applyToDashboardAndAllPages() {
    if (this.context !== NomObjectClass.DASHBOARD) return;

    this.applyDateRange();
    this.pagePersistence.clearAllPagePeriods();
  }

  onDateRangeSelection(range: { from: Date; to: Date; preset: string | null }) {
    this.fromDate = range.from;
    this.toDate = range.to;
    this.pendingPreset = range.preset;
  }

  /** "Last 1 hour" for a now-relative preset, null for a calendar preset or a hand-picked range. */
  relativeLabel(preset: string | null): string | null {
    const minutes = presetMinutes(preset);
    return minutes === null
      ? null
      : relativePeriodLabel(minutes, this.translateService);
  }

  onHistoryPeriodSelected(period: PeriodHistoryEntry) {
    // Legacy history entries may carry seconds; strip them so the applied
    // period stays a minute-stable query cache key (see normalizeRange in
    // DateRangeSelectionComponent).
    this.fromDate = startOfMinute(new Date(period.startTimestamp * 1000));
    this.toDate = endOfMinute(new Date(period.endTimestamp * 1000));
    this.pendingPreset = null;
    this.panelPreset = null;
  }

  onHistoryPeriodRemoved(period: PeriodHistoryEntry) {
    this.store.dispatch(removePeriodFromHistory({ period }));
  }

  cancel() {
    if (this.dropdown) {
      this.dropdown.close();
    }
  }

  applyDefaultPeriod() {
    this.store.dispatch(applyDefaultPeriod());
    this.cancel();
  }

  back() {
    this.store.dispatch(selectPreviousPeriod());
  }
}
